import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  useMediaQuery,
  MenuItem,
  Menu,
  Popper,
  Paper,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import { Box, useTheme, styled } from "@mui/system";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../assets/reeflogo.jpg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { apiList, invokeApi } from "../services/apiServices";
import { config } from "../config/config";
import { useCookies } from "react-cookie";
import { useNav } from "../context/NavContext";

const LogoWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  transition: "all 0.3s ease-in-out",
}));

const LogoImage = styled("img")(({ theme }) => ({
  transition: "all 0.3s ease-in-out",
  height: 80,
  [theme.breakpoints.down("sm")]: {
    height: 60,
  },
}));

const TopBar = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  padding: "10px 60px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "1px solid #f0f0f0",
  [theme.breakpoints.down("md")]: {
    padding: "10px 20px",
    flexDirection: "row",
  },
}));

const BrandingText = styled(Box)(({ theme }) => ({
  textAlign: "left",
  fontFamily: "'Inter', 'Montserrat', sans-serif",
  color: "#333",
  [theme.breakpoints.down("sm")]: {
    textAlign: "left",
    marginLeft: "10px",
  },
}));

const StyledAppBar = styled(AppBar)(({ theme, scrolled }) => ({
  backgroundColor: scrolled ? "rgba(255, 255, 255, 0.98)" : "#ffffff",
  boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.08)" : "none",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  borderBottom: scrolled ? "none" : "1px solid #eeeeee",
  position: scrolled ? "fixed" : "relative",
  top: 0,
  zIndex: 1200,
}));

const NavLink = styled(Box)(({ theme, active }) => ({
  padding: "18px 24px",
  fontSize: "14px",
  fontWeight: 700,
  color: active ? "#c62828" : "#212121",
  textTransform: "uppercase",
  cursor: "pointer",
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  letterSpacing: "0.5px",
  transition: "all 0.3s ease",
  "&:hover": {
    color: "#c62828",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    width: active ? "100%" : "0%",
    height: "3px",
    backgroundColor: "#c62828",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    transform: "translateX(-50%)",
  },
  "&:hover::after": {
    width: "100%",
  },
  [theme.breakpoints.down("md")]: {
    padding: "15px 12px",
    fontSize: "12px",
  },
}));

const Ripple = styled("span")(({ theme, delay = 0 }) => ({
  position: "absolute",
  top: 140,
  left: 137,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  transform: "translate(-50%, -50%)",
  animation: `heroripple 8s ${delay}s linear infinite`,
  zIndex: -1,
  pointerEvents: "none",
  width: "60px",
  height: "60px",

  [theme.breakpoints.down("xs")]: {
    left: 20,
  },

  [theme.breakpoints.down("sm")]: {
    left: 70,
    top: 70,
  },

  [theme.breakpoints.up("md")]: {
    width: "80px",
    height: "80px",
  },

  "@keyframes heroripple": {
    "0%": {
      transform: "translate(-50%, -50%) scale(2)",
      opacity: 0.4,
    },
    "100%": {
      transform: "translate(-50%, -50%) scale(4)",
      opacity: 0,
    },
  },
}));

const Header = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [scrolled, setScrolled] = useState(false);
  const [programmeAnchorEl, setProgrammeAnchorEl] = useState(null);
  const [publicationAnchorEl, setPublicationAnchorEl] = useState(null);
  const [peopleAnchorEl, setPeopleAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openProgrammeMobile, setOpenProgrammeMobile] = useState(false);
  const [openPublicationMobile, setOpenPublicationMobile] = useState(false);
  const [openPeopleMobile, setOpenPeopleMobile] = useState(false);
  const { ogUrl } = useParams();
  const { programmeItems, publicationItems, peopleItems, loading } = useNav();

  // Timeout references for hover delays
  const programmeTimeout = useRef(null);
  const publicationTimeout = useRef(null);
  const peopleTimeout = useRef(null);

  // Refs for anchor elements
  const publicationRef = useRef(null);

  // Generic Hover Handlers
  const handleMenuOpen = (anchorNode, setAnchor, timeoutRef) => {
    // Clear all timeouts
    [programmeTimeout, publicationTimeout, peopleTimeout].forEach(ref => {
      if (ref.current) clearTimeout(ref.current);
    });

    // Close other menus immediately
    if (setAnchor !== setProgrammeAnchorEl) setProgrammeAnchorEl(null);
    if (setAnchor !== setPublicationAnchorEl) setPublicationAnchorEl(null);
    if (setAnchor !== setPeopleAnchorEl) setPeopleAnchorEl(null);

    setAnchor(anchorNode);
  };

  const handleMenuClose = (setAnchor, timeoutRef) => {
    timeoutRef.current = setTimeout(() => {
      setAnchor(null);
    }, 100); // 100ms delay to allow moving mouse to the menu
  };

  const handleMenuEnter = (timeoutRef) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleMenuLeave = (setAnchor, timeoutRef) => {
    handleMenuClose(setAnchor, timeoutRef);
  };

  const handleScroll = () => {
    setScrolled(window.scrollY > 20);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
  }, [ogUrl]);

  const handleProgrammeItemClick = (path) => {
    navigate(path);
    setProgrammeAnchorEl(null);
  };

  const handlePublicationItemClick = (path) => {
    if (path) {
      if (path.startsWith("http")) {
        window.open(path, "_blank");
      } else {
        navigate(path);
      }
      setPublicationAnchorEl(null);
    }
  };

  const handlePeopleItemClick = (path) => {
    navigate(path);
    setPeopleAnchorEl(null);
  };

  // Mobile drawer handlers
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileItemClick = (path) => {
    if (path) {
      if (path.startsWith("http")) {
        window.open(path, "_blank");
      } else {
        navigate(path);
      }
      setMobileOpen(false);
    }
  };


  const drawer = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: "#000",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 3,
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          Menu
        </Typography>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: "#fff",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Drawer Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
        <List>
          {/* Home */}
          <ListItem
            button
            onClick={() => handleMobileItemClick("/")}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="Home"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* Programme Mobile Menu */}
          <ListItem
            button
            onClick={() => setOpenProgrammeMobile(!openProgrammeMobile)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="Programme"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {openProgrammeMobile ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </ListItem>
          <Collapse in={openProgrammeMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {programmeItems.map((item, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleMobileItemClick(item.path)}
                  sx={{
                    pl: 3,
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: "0.9rem" }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>

          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* Publication Mobile Menu */}
          <ListItem
            button
            onClick={() => setOpenPublicationMobile(!openPublicationMobile)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="Publication"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {openPublicationMobile ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </ListItem>
          <Collapse in={openPublicationMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {publicationItems.map((item, index) => (
                <div key={index}>
                  <ListItem
                    button
                    onClick={() =>
                      item.path && handleMobileItemClick(item.path)
                    }
                    sx={{
                      pl: 3,
                      borderRadius: 1,
                      mb: 0.5,
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                      }}
                    />
                  </ListItem>
                  {item.subitems && item.subitems.length > 0 && (
                    <List component="div" disablePadding>
                      {item.subitems.map((subitem, subIndex) => (
                        <ListItem
                          key={subIndex}
                          button
                          onClick={() => handleMobileItemClick(subitem.path)}
                          sx={{
                            pl: 5,
                            borderRadius: 1,
                            mb: 0.5,
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.08)",
                            },
                          }}
                        >
                          <ListItemText
                            primary={subitem.label}
                            primaryTypographyProps={{ fontSize: "0.85rem" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </div>
              ))}
            </List>
          </Collapse>

          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* People Mobile Menu */}
          <ListItem
            button
            onClick={() => setOpenPeopleMobile(!openPeopleMobile)}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="People"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {openPeopleMobile ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </ListItem>
          <Collapse in={openPeopleMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {peopleItems.map((item, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleMobileItemClick(item.path)}
                  sx={{
                    pl: 3,
                    borderRadius: 1,
                    mb: 0.5,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: "0.9rem" }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>

          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* Blog */}
          <ListItem
            button
            onClick={() => handleMobileItemClick("/blog")}
            sx={{
              borderRadius: 1,
              cursor: "pointer",
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="Blog"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* Gallery */}
          <ListItem
            button
            onClick={() => handleMobileItemClick("/photo")}
            sx={{
              borderRadius: 1,
              cursor: "pointer",
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="Gallery"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* Learn at REEF */}
          <ListItem
            component="div"
            onClick={() => {
              window.location.href =
                "https://web.classplusapp.com/login?orgCode=akqmvx";
            }}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="Learn at REEF"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>

          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.12)" }} />

          {/* Donate */}
          <ListItem
            button
            // onClick={() => handleMobileItemClick("/donate")}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            <ListItemText
              primary="Donate"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Logo and Branding Bar - Hidden on Mobile */}
      {!isMobile && (
        <TopBar>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", maxWidth: "1400px", margin: "0 auto", gap: "25px" }}>
            <LogoWrapper onClick={() => navigate("/")} sx={{ cursor: "pointer" }}>
              <LogoImage src={logo} alt="REEF Logo" />
            </LogoWrapper>
            <BrandingText>
              {/* <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#000546ff",
                  textTransform: "uppercase",
                  letterSpacing: "2.5px",
                  lineHeight: 1,
                  mb: 0.8,
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Research and Environmental
              </Typography> */}

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { md: "1.6rem", sm: "1.8rem" },
                  letterSpacing: "-0.5px",
                  lineHeight: 1,
                  fontFamily: "'Montserrat', sans-serif",
                  display: "flex",
                  color: "#000546ff",
                  textTransform: "uppercase",
                  alignItems: "baseline"
                }}
              >
                Research and Environmental Education Foundation
              </Typography>

              {/* <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: { md: "2.2rem", sm: "1.8rem" },
                  letterSpacing: "-1px",
                  lineHeight: 1,
                  fontFamily: "'Montserrat', sans-serif",
                  display: "flex",
                  color: "#000546ff",
                  textTransform: "uppercase",
                  alignItems: "baseline"
                }}
              >
                
              </Typography> */}
            </BrandingText>
          </Box>
        </TopBar>
      )}

      {/* Main Navigation Bar */}
      <StyledAppBar scrolled={scrolled} elevation={0}>
        <Toolbar
          sx={{
            minHeight: { xs: 60, md: 70 },
            display: "flex",
            justifyContent: "center",
            px: { xs: 2, md: 6 }
          }}
        >
          {isMobile ? (
            <Box sx={{
              display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center"
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
                  <LogoImage src={logo} alt="REEF Logo" sx={{ height: 40 }} />
                </Link>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: "#000546ff",
                      textTransform: "uppercase",
                      lineHeight: 1,
                      fontFamily: "'Montserrat', sans-serif"
                    }}
                  >
                    Research and Environmental
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#000546ff",
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      lineHeight: 1,
                      fontFamily: "'Montserrat', sans-serif",
                      mt: 0.5
                    }}
                  >
                    Education Foundation
                  </Typography>
                </Box>
              </Box>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  color: "#333",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "space-evenly",
                maxWidth: "1400px"
              }}
            >
              <NavLink onClick={() => navigate("/")} active={window.location.pathname === "/"}>
                Home
              </NavLink>

              {/* Programme Dropdown */}
              <Box
                onMouseEnter={(e) => handleMenuOpen(e.currentTarget, setProgrammeAnchorEl, programmeTimeout)}
                onMouseLeave={() => handleMenuClose(setProgrammeAnchorEl, programmeTimeout)}
              >
                <NavLink active={Boolean(programmeAnchorEl) || window.location.pathname.startsWith("/programme")}>
                  Programme
                  <KeyboardArrowDownIcon sx={{ fontSize: "1.1rem", transition: "transform 0.3s", transform: programmeAnchorEl ? "rotate(180deg)" : "rotate(0)" }} />
                </NavLink>
                <Popper
                  open={Boolean(programmeAnchorEl)}
                  anchorEl={programmeAnchorEl}
                  placement="bottom-start"
                  sx={{ zIndex: 1300, pt: 1 }}
                  onMouseEnter={() => handleMenuEnter(programmeTimeout)}
                  onMouseLeave={() => handleMenuLeave(setProgrammeAnchorEl, programmeTimeout)}
                >
                  {({ TransitionProps }) => (
                    <Paper
                      {...TransitionProps}
                      component={motion.div}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      transition={{ duration: 0.3 }}
                      sx={{
                        minWidth: "240px",
                        width: "auto",
                        borderRadius: "16px",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
                        border: "1px solid #f0f0f0",
                        p: 1.5,
                      }}
                    >
                      {programmeItems.map((item, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => handleProgrammeItemClick(item.path)}
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            py: 1.5,
                            px: 2,
                            borderRadius: "10px",
                            whiteSpace: "nowrap",
                            "&:hover": { backgroundColor: "#f8f9fa", color: "#c62828", pl: 2.5 },
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Paper>
                  )}
                </Popper>
              </Box>

              {/* Publication Dropdown */}
              <Box
                onMouseEnter={(e) => handleMenuOpen(e.currentTarget, setPublicationAnchorEl, publicationTimeout)}
                onMouseLeave={() => handleMenuClose(setPublicationAnchorEl, publicationTimeout)}
              >
                <NavLink active={Boolean(publicationAnchorEl) || window.location.pathname.startsWith("/publication")}>
                  Publication
                  <KeyboardArrowDownIcon sx={{ fontSize: "1.1rem", transition: "transform 0.3s", transform: publicationAnchorEl ? "rotate(180deg)" : "rotate(0)" }} />
                </NavLink>
                <Menu
                  anchorEl={publicationAnchorEl}
                  open={Boolean(publicationAnchorEl)}
                  onClose={() => setPublicationAnchorEl(null)}
                  MenuListProps={{
                    onMouseEnter: () => handleMenuEnter(publicationTimeout),
                    onMouseLeave: () => handleMenuLeave(setPublicationAnchorEl, publicationTimeout),
                  }}
                  PaperProps={{
                    sx: {
                      minWidth: "240px",
                      width: "auto",
                      borderRadius: "16px",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
                      border: "1px solid #f0f0f0",
                      p: 1.5,
                    },
                  }}
                >
                  {publicationItems.map((item, index) => (
                    <div key={index}>
                      <MenuItem
                        onClick={() => !item.subitems && handlePublicationItemClick(item.path)}
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          py: 1.2,
                          px: 2,
                          borderRadius: "10px",
                          color: "#1a1a1a",
                          whiteSpace: "nowrap",
                          "&:hover": { backgroundColor: "#f8f9fa", color: "#c62828" },
                        }}
                      >
                        {item.label}
                      </MenuItem>
                      {item.subitems && item.subitems.map((subitem, subIndex) => (
                        <MenuItem
                          key={subIndex}
                          onClick={(e) => { e.stopPropagation(); handlePublicationItemClick(subitem.path); }}
                          sx={{
                            fontSize: "13px",
                            fontWeight: 400,
                            py: 0.8,
                            px: 2,
                            ml: 2,
                            borderRadius: "10px",
                            color: "#1a1a1a",
                            whiteSpace: "nowrap",
                            "&:hover": { backgroundColor: "#f8f9fa", color: "#c62828" },
                          }}
                        >
                          • {subitem.label}
                        </MenuItem>
                      ))}
                    </div>
                  ))}
                </Menu>
              </Box>

              {/* People Dropdown */}
              <Box
                onMouseEnter={(e) => handleMenuOpen(e.currentTarget, setPeopleAnchorEl, peopleTimeout)}
                onMouseLeave={() => handleMenuClose(setPeopleAnchorEl, peopleTimeout)}
              >
                <NavLink active={Boolean(peopleAnchorEl) || window.location.pathname.startsWith("/people")}>
                  People
                  <KeyboardArrowDownIcon sx={{ fontSize: "1.1rem", transition: "transform 0.3s", transform: peopleAnchorEl ? "rotate(180deg)" : "rotate(0)" }} />
                </NavLink>
                <Popper
                  open={Boolean(peopleAnchorEl)}
                  anchorEl={peopleAnchorEl}
                  placement="bottom-start"
                  sx={{ zIndex: 1300, pt: 1 }}
                  onMouseEnter={() => handleMenuEnter(peopleTimeout)}
                  onMouseLeave={() => handleMenuLeave(setPeopleAnchorEl, peopleTimeout)}
                >
                  {({ TransitionProps }) => (
                    <Paper
                      {...TransitionProps}
                      component={motion.div}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 15 }}
                      transition={{ duration: 0.3 }}
                      sx={{
                        minWidth: "240px",
                        width: "auto",
                        borderRadius: "16px",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
                        border: "1px solid #f0f0f0",
                        p: 1.5,
                      }}
                    >
                      {peopleItems.map((item, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => handlePeopleItemClick(item.path)}
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            py: 1.5,
                            px: 2,
                            borderRadius: "10px",
                            whiteSpace: "nowrap",
                            "&:hover": { backgroundColor: "#f8f9fa", color: "#c62828", pl: 2.5 },
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Paper>
                  )}
                </Popper>
              </Box>

              {/* Blog */}
              <NavLink onClick={() => navigate("/blog")} active={window.location.pathname.startsWith("/blog")}>
                Blog
              </NavLink>

              {/* Gallery */}
              <NavLink onClick={() => navigate("/photo")} active={window.location.pathname.startsWith("/photo")}>
                Gallery
              </NavLink>

              {/* Learn at REEF */}
              <NavLink onClick={() => { window.location.href = "https://web.classplusapp.com/login?orgCode=akqmvx"; }}>
                Learn at REEF
              </NavLink>

              {/* Donate Button */}
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#c62828",
                  color: "#fff",
                  borderRadius: "50px",
                  px: 4,
                  py: 1,
                  fontSize: "13px",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  boxShadow: "0 4px 14px 0 rgba(198,40,40,0.39)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#b71c1c",
                    boxShadow: "0 6px 20px rgba(198,40,40,0.45)",
                    transform: "translateY(-1px)"
                  },
                }}
              >
                Donate
              </Button>
            </Box>
          )}
        </Toolbar>
      </StyledAppBar >

      {/* Mobile Drawer */}
      < Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }
        }
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "85%", sm: "350px" },
          },
        }}
      >
        {drawer}
      </Drawer >
    </>
  );
};

export default Header;
